/**
 * AddAgencyReviewModal — create or edit an Agency Review (Airtable Agency Approvals).
 *
 * Opens via CustomEvent('open-add-agency-review-modal', { detail }):
 *   create: { permitId }
 *   edit:   { reviewId, prefill: { agencyOrgId, trackingNumber, status, milestoneIds } }
 *
 * Agency is an Airtable Organization (picker via getOrganizations / ctx.orgsCache).
 * Milestone(s) (2026-07) — which of the project's PERMIT_MS_TYPES milestones this review
 * shows under (PermitsSection's "Permits" sub-section). Never auto-populated; a review
 * with none set simply never appears under any milestone until linked here. A review can
 * legitimately span several (initial submission + later resubmission milestones).
 * On success busts ctx.permitReviewCache and dispatches outline-render.
 */

import { useState, useEffect, useRef } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { createAgencyReview, updateAgencyReview, getPermitMilestoneCandidates } from '../services/permits.js'
import { getOrganizations } from '../services/design.js'
import Select from './Select.jsx'

const REVIEW_STATUSES = [
  'Preparing to Submit', 'Submitted - Intake in Process', 'Items Requested', 'Under Review',
  'Making Corrections', 'Preparing for Hearing', 'Appeal Period', 'Approved',
  'Permit Revision - Under Review', 'Permit Revision - Making Corrections',
  'Permit Revision - Approved', 'Cancelled',
]

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none',
}

export default function AddAgencyReviewModal() {
  const [open,       setOpen]       = useState(false)
  const [permitId,   setPermitId]   = useState(null)
  const [editId,     setEditId]     = useState(null)
  const [orgId,      setOrgId]      = useState('')
  const [tracking,   setTracking]   = useState('')
  const [status,     setStatus]     = useState('Preparing to Submit')
  const [orgs,       setOrgs]       = useState(null)
  const [msCandidates, setMsCandidates] = useState(null)
  const [milestoneIds, setMilestoneIds] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState(null)
  const orgsLoaded = useRef(false)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setPermitId(d.permitId || null)
      setEditId(d.reviewId || null)
      setOrgId(d.prefill?.agencyOrgId || '')
      setTracking(d.prefill?.trackingNumber || '')
      setStatus(d.prefill?.status || 'Preparing to Submit')
      setMilestoneIds(d.prefill?.milestoneIds || [])
      setError(null); setSubmitting(false); setOpen(true)

      if (ctx.orgsCache && ctx.orgsCache.length > 0) setOrgs(ctx.orgsCache)
      else if (!orgsLoaded.current) {
        orgsLoaded.current = true
        getOrganizations().then(list => { ctx.orgsCache = list; setOrgs(list) }).catch(() => setOrgs([]))
      }

      const projectId = ctx.recordId || wf.zoomProjectId || ''
      setMsCandidates(null)
      if (projectId) {
        getPermitMilestoneCandidates(projectId).then(setMsCandidates).catch(() => setMsCandidates([]))
      } else {
        setMsCandidates([])
      }
    }
    window.addEventListener('open-add-agency-review-modal', onOpen)
    return () => window.removeEventListener('open-add-agency-review-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    if (!orgId) return
    setSubmitting(true); setError(null)
    try {
      if (editId) {
        const f = { 'Agency': [orgId], 'Status': status, 'Project Deliverables': milestoneIds }
        if (tracking) f['Agency Tracking Number'] = tracking
        await updateAgencyReview(editId, f)
      } else {
        if (!permitId) { setError('No permit in context.'); setSubmitting(false); return }
        await createAgencyReview(permitId, { agencyOrgId: orgId, trackingNumber: tracking, status, milestoneIds })
      }
      ctx.permitReviewCache = undefined
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save agency review'); setSubmitting(false)
    }
  }

  if (!open) return null
  const canSubmit = orgId && !submitting

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddAgencyReviewModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 420, padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            {editId ? 'Edit Agency Review' : 'Add Agency Review'}
          </span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Agency <span style={{ color: '#c0392b' }}>*</span></label>
            {orgs === null ? (
              <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif' }}>Loading organizations…</div>
            ) : (
              <Select value={orgId} onChange={setOrgId} options={(orgs || []).map(o => ({ value: o.id, label: o.name }))}
                placeholder="Select agency…" searchable style={{ width: '100%', fontSize: 12 }} />
            )}
          </div>
          <div>
            <label style={labelStyle}>Agency Tracking Number</label>
            <input style={inputStyle} value={tracking} onChange={e => setTracking(e.target.value)} placeholder="Optional" />
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <Select value={status} onChange={setStatus} options={REVIEW_STATUSES.map(s => ({ value: s, label: s }))}
              placeholder="Select status…" style={{ width: '100%', fontSize: 12 }} />
          </div>
          <div>
            <label style={labelStyle}>Milestone(s)</label>
            <div style={{ fontSize: 10.5, color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif', marginBottom: 6, lineHeight: 1.4 }}>
              Which milestone(s) this review shows under — e.g. an initial submission and, later, a resubmission.
            </div>
            {msCandidates === null ? (
              <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif' }}>Loading milestones…</div>
            ) : msCandidates.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif' }}>No permit-type milestones found on this project.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 140, overflowY: 'auto' }}>
                {msCandidates.map(m => (
                  <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={milestoneIds.includes(m.id)}
                      onChange={() => setMilestoneIds(ids => ids.includes(m.id) ? ids.filter(x => x !== m.id) : [...ids, m.id])}
                    />
                    {m.name}
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
              {submitting ? 'Saving…' : (editId ? 'Save' : 'Add Review')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
